import { useMemo } from 'react';
import {
  printerStatus,
  stationStatus,
  type ShipNodeStation,
  type StatusLight,
  useShipNodeStations,
} from '@skupreme/shared/components/shipnode';
import ShipNodeService from '@utility/services/shipnode';

/* What the print popups offer: the organization's paired ShipNode stations (the station backend, refreshed
   every 15 s while a popup is open). The 2023 app's Firestore computers were retired in EN-3208. */

export type PrintStationPrinter = {
  id: string;
  name: string;
  online: boolean;
  status: StatusLight;
};

export type PrintStation = {
  id: string;
  name: string;
  online: boolean;
  status: StatusLight;
  printers: PrintStationPrinter[];
};

const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name);

function fromPaired(station: ShipNodeStation, now: Date): PrintStation {
  return {
    id: station.id,
    name: station.name || 'ShipNode station',
    online: station.online,
    status: stationStatus(station, now),
    printers: station.printers
      .map(printer => ({
        id: printer.id,
        name: printer.displayName || printer.id,
        online: printer.online,
        status: printerStatus(printer),
      }))
      .sort(byName),
  };
}

/** Online stations first, then offline, then by name. */
export function toPrintStations(
  paired: ShipNodeStation[],
  now: Date = new Date(),
): PrintStation[] {
  return paired
    .map(station => fromPaired(station, now))
    .sort((a, b) => Number(!a.online) - Number(!b.online) || byName(a, b));
}

/** The stations a print popup lists. `open` false (popup closed) skips the station-backend requests. */
export function usePrintStations(open: boolean) {
  const paired = useShipNodeStations(ShipNodeService, undefined, open);

  const stations = useMemo(
    () => toPrintStations(paired.stations),
    [paired.stations],
  );

  return {
    stations,
    loading: open && paired.loading,
    error: paired.error,
  };
}
